/* eslint-disable no-control-regex */
import { helper } from '@ember/component/helper';
import { htmlSafe as EmberHtmlSafe } from '@ember/template';

// Elements that can execute script or load external content. Captured snapshot
// markup is only ever SVG / table / text, so none of these are legitimate.
const FORBIDDEN_TAGS = [
  'script',
  'style',
  'iframe',
  'object',
  'embed',
  'link',
  'meta',
  'base',
  'form',
  'img',
  'a',
  'use',
  'foreignobject',
  'animate',
  'animatemotion',
  'animatetransform',
  'set',
];

// Attributes carrying a URL we must screen for the javascript: scheme.
const URL_ATTRS = ['href', 'xlink:href', 'src', 'action', 'formaction'];

// Inline-style properties the snapshot legitimately uses. Anything else is
// dropped. background-color / color / min-width cover the strand table and the
// progress-visual header; fill / stroke / stop-color / stop-opacity cover the
// captured chart SVGs — chart-stacked-area sets the area fill (sometimes a
// plain colour, sometimes a url(#gradient-…) reference) and the gradient
// <stop> colour/opacity inline via d3 .style(), so without these the
// progress-visual renders black/blank. Keep in lockstep with the server-side
// sanitiser (HERO-13205): both must allow the same props or snapshots render
// wrong depending on which side stripped them.
const ALLOWED_STYLE_PROPS = [
  'background-color',
  'color',
  'min-width',
  'fill',
  'stroke',
  'stop-color',
  'fill-opacity',
  'stroke-opacity',
  'stop-opacity',
  'opacity',
  'stroke-width',
];

function isUnsafeUrl(value) {
  // Browsers ignore leading/embedded control chars and whitespace when
  // resolving a URL scheme (e.g. "java\tscript:"), so strip them before test.
  let normalised = (value || '').replace(/[\x00-\x20]+/g, '');
  return /^(javascript|vbscript|data):/i.test(normalised);
}

// SVG paint props may reference in-document defs via url(#id) — gradients,
// clip-paths, patterns. Those are same-document and safe. Any other url()
// (external href, data:, scheme, protocol-relative) is rejected: like
// background-image it could trigger an out-of-origin fetch.
function hasExternalUrl(value) {
  let matches = value.match(/url\(\s*['"]?[^'")]*/gi);
  if (!matches) {
    return false;
  }
  return matches.some((match) => {
    let inner = match.replace(/^url\(\s*['"]?/i, '').trim();
    return !inner.startsWith('#');
  });
}

// Keep only allowlisted declarations; reject external url() values defensively
// (same-document url(#id) refs are allowed — see hasExternalUrl).
function sanitizeStyle(value) {
  return (value || '')
    .split(';')
    .map((decl) => decl.trim())
    .filter(Boolean)
    .filter((decl) => {
      let colon = decl.indexOf(':');
      if (colon === -1) {
        return false;
      }
      let prop = decl.slice(0, colon).trim().toLowerCase();
      let propValue = decl.slice(colon + 1);
      return ALLOWED_STYLE_PROPS.includes(prop) && !hasExternalUrl(propValue);
    })
    .join('; ');
}

function sanitizeElement(el) {
  // Drop event-handler attributes (onclick, onload, …) and unsafe-scheme URLs.
  for (let attr of Array.from(el.attributes)) {
    let name = attr.name.toLowerCase();
    if (name.startsWith('on')) {
      el.removeAttribute(attr.name);
    } else if (URL_ATTRS.includes(name) && isUnsafeUrl(attr.value)) {
      el.removeAttribute(attr.name);
    } else if (name === 'style') {
      let safe = sanitizeStyle(attr.value);
      if (safe) {
        el.setAttribute('style', safe);
      } else {
        el.removeAttribute(attr.name);
      }
    }
  }

  for (let child of Array.from(el.children)) {
    if (FORBIDDEN_TAGS.includes(child.tagName.toLowerCase())) {
      child.remove();
    } else {
      sanitizeElement(child);
    }
  }
}

// Snapshot visuals (progressVisual / strandTableVisual / strandSliderVisual)
// are persisted raw HTML rendered via htmlSafe. Although captured from our own
// DOM at publish time, a direct API write could inject arbitrary markup, so we
// strip script-capable elements and event-handler attributes before trusting it.
export function safeSnapshotHtml([inStr]) {
  if (!inStr) {
    return EmberHtmlSafe('');
  }
  let doc = new DOMParser().parseFromString(String(inStr), 'text/html');
  sanitizeElement(doc.body);
  return EmberHtmlSafe(doc.body.innerHTML);
}

export default helper(safeSnapshotHtml);
